import { runInAction } from 'mobx';
import React from 'react';

import { ApplicationRoutes } from '../../../../../application/constants/routes';
import { ILocalization, ILocalizationToken } from '../../../../../application/language/ILocalization';
import { inject } from '../../../../../common/container/inject';
import { IError } from '../../../../../common/error/IError';
import { ILearningRootService, LearningRootServiceToken } from '../../../../../service/LearningRootService';
import { injectRootService } from '../../../../../service/RootServiceFactory';
import { IRouterService, RouterServiceToken } from '../../../../../service/route/IRouterService';
import { IUnitCardModel } from '../../../../../service/unit/interface/IUnitCardModel';
import { ConfirmModal } from '../../../../design/modal/confirm/ConfirmModal';
import { IMainLayoutDomainStore } from '../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { LANGUAGES_MAP } from '../components/UnitCardLangs';
import { IUnitListAdminPageDomain } from './IUnitListAdminPageDomain';
import { IUnitListAdminPageUI } from './IUnitListAdminPageUI';
import { UnitListAdminPageUI } from './UnitListAdminPageUI';

export class UnitListAdminPageDomain implements IUnitListAdminPageDomain {
  public ui: IUnitListAdminPageUI;

  constructor(
    public layoutDomain: IMainLayoutDomainStore,
    public router: IRouterService = inject<IRouterService>(RouterServiceToken),
    private learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
    private rootService = injectRootService(layoutDomain.serviceType.value),
    public i18n: ILocalization = inject<ILocalization>(ILocalizationToken),
  ) {
    this.ui = new UnitListAdminPageUI();
  }
  publishUnit = async (id: string) => {
    await this.learningRootService.unit.publishByModel(id).catch(this.dataLoadErrorCatch);

    const unit = this.ui.unitList.list.find((u) => u.id === id);
    if (unit) {
      unit.isPublished = true;
      unit.wasPublished = true;
    }
  };

  unpublishUnit = async (id: string) => {
    await this.learningRootService.unit.unpublishByModel(id).catch(this.dataLoadErrorCatch);

    const unit = this.ui.unitList.list.find((u) => u.id === id);
    if (unit) unit.isPublished = false;
  };

  loadData = async () => {
    const params = new URLSearchParams(window.location.search);
    const initialQuery = params.get('q') || '';

    this.ui.searchText.setValue(initialQuery);

    this.ui.isLoading.setValue(true);

    const searchResult = await this.learningRootService.unit.getListCards({ limit: 100000 });

    const { data: specificationCategoryData } = await this.rootService.specification.category.search({
      fields: ['name', 'id'],
      limit: 100000,
    });

    const { data: characteristicData } = await this.rootService.specification.entity.search({
      limit: 10000,
      fields: ['name', 'categoryId'],
    });

    runInAction(() => {
      this.ui.unitList.setList(searchResult ?? []);
      this.ui.specificationCategory.setList(specificationCategoryData);
      this.ui.characteristic.setList(characteristicData);
    });

    if (characteristicData) {
      setTimeout(() => {
        this.ui.isLoading.setValue(false);
      }, 300);
    }

    this.createdGroupedList();
    this.ui.filtredGroupedUnits.setList(this.ui.groupedUnits.list);

    if (initialQuery) {
      this.handleSearchByGroupsAndName(initialQuery, { updateUrl: false });
    }
  };

  createdGroupedList = () => {
    const charIdToSpecNameMap = this.ui.characteristic.list.reduce<Record<string, string>>((map, char) => {
      const spec = this.ui.specificationCategory.list.find((spec) => spec.id === char.categoryId);
      if (spec?.name && char.id) {
        map[char.id] = spec.name;
      }
      return map;
    }, {});

    const groups: Record<string, IUnitCardModel[]> = {
      [this.i18n.translate('learningAdminPage.withoutContext')]: [],
    };

    this.ui.unitList.list.forEach((unit) => {
      const charIds = unit.settings.characteristicsIds;
      if (charIds && charIds.length > 0) {
        const charId = charIds[0];
        const groupName = charIdToSpecNameMap[charId] || charId;
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push(unit);
      } else {
        groups[this.i18n.translate('learningAdminPage.withoutContext')].push(unit);
      }
    });

    const sortedGroupList = Object.keys(groups)
      .map((groupName) => ({
        groupName,
        unitsList: groups[groupName],
      }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));

    this.ui.groupedUnits.setList(sortedGroupList);
  };

  createUnit = async (): Promise<string> => {
    const result = await this.learningRootService.unit
      .createByModel(this.ui.unit.entity)
      .catch(this.dataLoadErrorCatch);

    return typeof result === 'string' ? result : '';
  };

  onEditUnit = async (unitId: string) => {
    this.router.goTo(ApplicationRoutes.LearningUnitEdit.replace(':unitId', unitId));
    await this.loadData();
  };

  deleteUnit = (unitId: string) => {
    const Component = () => {
      return (
        <ConfirmModal
          title={this.i18n.translate('learningAdminPage.deleteUnit')}
          text={this.i18n.translate('learningAdminPage.confirmDeleteUnit')}
          onCancel={async () => {
            this.layoutDomain.modalWindow.closeModalWindow();
          }}
          onOk={async () => {
            await this.learningRootService.unit.deleteById(unitId);
            this.ui.groupedUnits.setList([
              { groupName: this.i18n.translate('learningAdminPage.withoutContext'), unitsList: [] },
            ]);
            await this.loadData();
            this.layoutDomain.modalWindow.closeModalWindow();
          }}
        />
      );
    };
    return Component;
  };

  onDeleteUnit = (unitId: string) => {
    this.layoutDomain.modalWindow.showModalWindow(this.deleteUnit(unitId));
  };

  redirectToCreateUnit = () => {
    this.createUnit().then((unitId) => {
      if (unitId) {
        this.router.goTo(ApplicationRoutes.LearningUnitCreate.replace(':unitId', unitId));
      }
    });
  };

  handleSearchByGroupsAndName = (searchText: string, options: { updateUrl?: boolean } = { updateUrl: true }) => {
    const normalize = (str: string | undefined | null): string =>
      (str ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ''); // оставляем только буквы и цифры

    const trimmedOriginal = searchText.toLowerCase();
    const normalizedSearch = normalize(searchText);

    this.ui.searchText.setValue(searchText);

    if (options.updateUrl) {
      const params = new URLSearchParams(window.location.search);
      trimmedOriginal ? params.set('q', trimmedOriginal) : params.delete('q');

      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    if (!normalizedSearch) {
      this.ui.filtredGroupedUnits.setList(this.ui.groupedUnits.list);
      return;
    }

    const getCweStrictToken = () => {
      const match = trimmedOriginal.match(/^cwe[\s\-_]*([0-9]+)$/i);
      if (!match) return null;

      const num = match[1];
      return `cwe${num}`; // нормализованный вид токена
    };

    const unitMatches = (unit: IUnitCardModel) => {
      const { titleHeading, titleLead, description } = unit.settings;

      const normalizedHeading = normalize(titleHeading);
      const normalizedLead = normalize(titleLead);
      const normalizedDesc = normalize(description);

      // --- Строгий cwe поиск
      const cweStrictSearch = getCweStrictToken();
      if (cweStrictSearch) {
        const cweTokens = normalizedDesc.match(/cwe[0-9]+/g) ?? [];

        return cweTokens.includes(cweStrictSearch);
      }

      // --- Обычный нестрогий поиск
      const searchableText = normalizedHeading + normalizedLead + normalizedDesc;

      const languages = unit.practice?.languages ?? [];
      const languagesMatch = languages.some((code) => {
        const meta = LANGUAGES_MAP[code];
        if (!meta) return false;

        return code.toLowerCase().includes(trimmedOriginal) || meta.title.toLowerCase().includes(trimmedOriginal);
      });

      return languagesMatch || searchableText.includes(normalizedSearch);
    };

    const filteredGroups = this.ui.groupedUnits.list
      .map((group) => {
        const normalizedGroupName = normalize(group.groupName);
        const groupMatches = normalizedGroupName.includes(normalizedSearch);

        const filteredUnits = group.unitsList.filter(unitMatches);

        return {
          ...group,
          unitsList: groupMatches ? group.unitsList : filteredUnits,
        };
      })
      .filter((group) => group.unitsList.length > 0);

    this.ui.filtredGroupedUnits.setList(filteredGroups);
  };

  handleFilterByIsPublished = (filter: string) => {
    this.ui.filterByIsPublished.setValue(filter);
  };

  handleFiterByIsPublished;
  private dataLoadErrorCatch = (error: IError) => {
    this.layoutDomain.errors.handleError(error);
    return {
      data: [],
    };
  };

  updateUnitsProjectRoles = async (roleId: string, unitIds: string[]) => {
    this.learningRootService.unit
      .updateProjectRoles({
        roleId,
        unitIds,
      })
      .catch((err) => {
        console.warn(err);
      });
  };
}
